import { useState, useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Link, useNavigate } from 'react-router';
import { Logo } from '@elements/Logo/Logo';
import { LogoTablet } from '@elements/Logo/LogoTablet';
import Button from '@elements/Button/Button';
import Icon from '@elements/Icon/Icon';
import phoneIcon from '@/assets/icons/phone.svg';
import menuIcon from '@/assets/icons/menu.svg';
import closeIcon from '@/assets/icons/close.svg';
import chevronIcon from '@/assets/icons/chevron.svg';
import {
    buildShopPlansStateRootPath,
    SHOP_PLANS_BASE_PATH,
} from '@/lib/shopPlansRouting';

export function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isShopPlansDropdownOpen, setIsShopPlansDropdownOpen] = useState(false);
    const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
    const [isUseCasesDropdownOpen, setIsUseCasesDropdownOpen] = useState(false);
    const [isShopPlansStickyBarActive, setIsShopPlansStickyBarActive] = useState(false);
    const headerRef = useRef<HTMLElement>(null);
    const shopPlansDropdownRef = useRef<HTMLDivElement>(null);
    const companyDropdownRef = useRef<HTMLDivElement>(null);
    const useCasesDropdownRef = useRef<HTMLDivElement>(null);
    const closeShopPlansTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const closeCompanyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const closeUseCasesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const pathname = usePathname() ?? '/';
    const navigate = useNavigate();
    const isShopPlansRoute = pathname.startsWith(SHOP_PLANS_BASE_PATH);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    const handleShopPlansDropdownToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsShopPlansDropdownOpen(!isShopPlansDropdownOpen);
    };

    const handleCompanyDropdownToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsCompanyDropdownOpen(!isCompanyDropdownOpen);
    };

    const handleUseCasesDropdownToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsUseCasesDropdownOpen(!isUseCasesDropdownOpen);
    };

    const handleUseCasesMouseEnter = () => {
        if (closeUseCasesTimeoutRef.current) {
            clearTimeout(closeUseCasesTimeoutRef.current);
            closeUseCasesTimeoutRef.current = null;
        }
        setIsUseCasesDropdownOpen(true);
    };

    const handleUseCasesMouseLeave = () => {
        closeUseCasesTimeoutRef.current = setTimeout(() => {
            setIsUseCasesDropdownOpen(false);
            closeUseCasesTimeoutRef.current = null;
        }, 250);
    };

    const handleCompanyMouseEnter = () => {
        if (closeCompanyTimeoutRef.current) {
            clearTimeout(closeCompanyTimeoutRef.current);
            closeCompanyTimeoutRef.current = null;
        }
        setIsCompanyDropdownOpen(true);
    };

    const handleCompanyMouseLeave = () => {
        closeCompanyTimeoutRef.current = setTimeout(() => {
            setIsCompanyDropdownOpen(false);
            closeCompanyTimeoutRef.current = null;
        }, 250);
    };

    const handleShopPlansMouseEnter = () => {
        if (closeShopPlansTimeoutRef.current) {
            clearTimeout(closeShopPlansTimeoutRef.current);
            closeShopPlansTimeoutRef.current = null;
        }
        setIsShopPlansDropdownOpen(true);
    };

    const handleShopPlansMouseLeave = () => {
        closeShopPlansTimeoutRef.current = setTimeout(() => {
            setIsShopPlansDropdownOpen(false);
            closeShopPlansTimeoutRef.current = null;
        }, 250);
    };

    const handleCompanyTabNavigation = (tab: string) => {
        if (tab === 'overview') {
            navigate('/company/overview');
        } else if (tab === 'founders') {
            navigate('/company/founders');
        } else if (tab === 'advisory-board') {
            navigate('/company/advisors');
        } else if (tab === 'press') {
            navigate('/company/press-pr-media-kit');
        }
    };

    const handleMobileCompanyTabNavigation = (tab: string) => {
        handleCompanyTabNavigation(tab);
        setIsCompanyDropdownOpen(false);
        closeMenu();
    };

    const handleUseCasesNavigation = (path: string) => {
        navigate(path, { preventScrollReset: true });
    };

    const handleMobileUseCasesNavigation = (path: string) => {
        navigate(path, { preventScrollReset: true });
        setIsUseCasesDropdownOpen(false);
        closeMenu();
    };

    const handleTabNavigation = (tab: string) => {
        if (tab === 'state-county') {
            navigate(buildShopPlansStateRootPath());
        } else {
            navigate(`${SHOP_PLANS_BASE_PATH}?tab=${tab}`);
        }
        setIsShopPlansDropdownOpen(false);
        closeMenu();
    };

    useEffect(() => {
        return () => {
            if (closeShopPlansTimeoutRef.current) {
                clearTimeout(closeShopPlansTimeoutRef.current);
            }
            if (closeCompanyTimeoutRef.current) {
                clearTimeout(closeCompanyTimeoutRef.current);
            }
            if (closeUseCasesTimeoutRef.current) {
                clearTimeout(closeUseCasesTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const headerEl = headerRef.current;
        if (!headerEl) return;

        const updateHeaderHeight = () => {
            document.documentElement.style.setProperty('--site-header-height', `${headerEl.offsetHeight}px`);
        };

        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);

        if (typeof ResizeObserver === 'undefined') {
            return () => {
                window.removeEventListener('resize', updateHeaderHeight);
            };
        }

        const resizeObserver = new ResizeObserver(updateHeaderHeight);
        resizeObserver.observe(headerEl);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleStickyBarStateChange = (event: Event) => {
            const nextActive = Boolean((event as CustomEvent<{ active?: boolean }>).detail?.active);
            setIsShopPlansStickyBarActive(nextActive);
        };

        window.addEventListener('shop-plans-sticky-change', handleStickyBarStateChange as EventListener);

        return () => {
            window.removeEventListener('shop-plans-sticky-change', handleStickyBarStateChange as EventListener);
        };
    }, []);

    useEffect(() => {
        if (!isShopPlansRoute) {
            setIsShopPlansStickyBarActive(false);
        }
    }, [isShopPlansRoute]);

    useEffect(() => {
        if (!isShopPlansRoute || !isShopPlansStickyBarActive) {
            return;
        }

        setIsMenuOpen(false);
        setIsShopPlansDropdownOpen(false);
        setIsCompanyDropdownOpen(false);
        setIsUseCasesDropdownOpen(false);
    }, [isShopPlansRoute, isShopPlansStickyBarActive]);

    return (
        <header
            ref={headerRef}
            data-site-header
            className={`sticky top-0 z-50 bg-white shadow-sm transition-transform duration-200 ${isShopPlansRoute && isShopPlansStickyBarActive ? '-translate-y-full pointer-events-none' : 'translate-y-0'}`}
        >
            <div className="bg-brand-100 hidden md:block">
                <div className="container mx-auto px-4 py-1">
                    <div className="flex items-center justify-between md:justify-end lg:justify-end gap-4">
                        <div className="flex items-center gap-2">
                            <Icon src={phoneIcon} className="size-4 text-primary" />
                            <a href="tel:+1-888-777-8190" className="text-sm text-primary">+1-888-777-8190</a>
                        </div>
                        <Link to="https://app.predictablebenefits.com/auth/login" className="text-sm text-primary">
                            Log In
                        </Link>
                    </div>
                </div>
            </div>
            <nav className="container mx-auto px-4 py-4">
                <div className="flex items-center justify-between">
                    <Link to="/" className="text-xl font-bold" onClick={closeMenu}>
                        {/* Mobile */}
                        <Logo className="h-12 md:hidden" />
                        {/* Tablet */}
                        <LogoTablet className="hidden md:inline-block lg:hidden" />
                        {/* Desktop */}
                        <Logo className="hidden lg:block h-12" />
                    </Link>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex gap-6">
                        <Link to="/" className="px-3 py-2 rounded-lg transition-all hover:bg-brand-50 hover:text-brand-700">
                            Home
                        </Link>
                        <div
                            className="relative"
                            ref={shopPlansDropdownRef}
                            onMouseEnter={handleShopPlansMouseEnter}
                            onMouseLeave={handleShopPlansMouseLeave}
                        >
                            <button
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-all hover:text-brand-700 ${isShopPlansDropdownOpen ? 'border border-brand-700 text-brand-700' : 'border border-transparent hover:border-brand-700'}`}
                            >
                                <span>Shop Plans</span>
                                <Icon
                                    src={chevronIcon}
                                    className={`size-4 transition-transform duration-200 ${isShopPlansDropdownOpen ? 'rotate-180' : ''}`}
                                />
                            </button>
                            {isShopPlansDropdownOpen && (
                                <div className="absolute top-full p-2 left-0 mt-2 w-[230px] bg-white rounded-lg shadow-lg border border-brand-700 z-9999">
                                    <div className="py-2">
                                        <button
                                            onClick={() => handleTabNavigation('age-zip')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            Search by Zip & Age
                                        </button>
                                        <button
                                            onClick={() => handleTabNavigation('state-county')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            Browse by State & County
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <Link to="/pricing" className="px-3 py-2 rounded-lg transition-all hover:bg-brand-50 hover:text-brand-700">
                            Pricing
                        </Link>
                        <div
                            className="relative"
                            ref={companyDropdownRef}
                            onMouseEnter={handleCompanyMouseEnter}
                            onMouseLeave={handleCompanyMouseLeave}
                        >
                            <button
                                type="button"
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-all hover:text-brand-700 ${isCompanyDropdownOpen ? 'border border-brand-700 text-brand-700' : 'border border-transparent hover:border-brand-700'}`}
                            >
                                <span>Company</span>
                                <Icon
                                    src={chevronIcon}
                                    className={`size-4 transition-transform duration-200 ${isCompanyDropdownOpen ? 'rotate-180' : ''}`}
                                />
                            </button>
                            {isCompanyDropdownOpen && (
                                <div className="absolute top-full p-2 left-0 mt-2 w-[230px] bg-white rounded-lg shadow-lg border border-brand-700 z-9999">
                                    <div className="py-2 flex flex-col gap-2">
                                        <button
                                            onClick={() => handleCompanyTabNavigation('overview')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            Overview
                                        </button>
                                        <button
                                            onClick={() => handleCompanyTabNavigation('founders')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            Founders
                                        </button>
                                        <button
                                            onClick={() => handleCompanyTabNavigation('advisory-board')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            Advisory Board
                                        </button>
                                        <button
                                            onClick={() => handleCompanyTabNavigation('press')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            Press
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div
                            className="relative"
                            ref={useCasesDropdownRef}
                            onMouseEnter={handleUseCasesMouseEnter}
                            onMouseLeave={handleUseCasesMouseLeave}
                        >
                            <Link
                                to="/use-cases/overview"
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-all hover:text-brand-700 ${isUseCasesDropdownOpen ? 'border border-brand-700 text-brand-700' : 'border border-transparent hover:border-brand-700'}`}
                            >
                                <span>Use Cases</span>
                                <Icon
                                    src={chevronIcon}
                                    className={`size-4 transition-transform duration-200 ${isUseCasesDropdownOpen ? 'rotate-180' : ''}`}
                                />
                            </Link>
                            {isUseCasesDropdownOpen && (
                                <div className="absolute top-full p-2 left-0 mt-2 w-[230px] bg-white rounded-lg shadow-lg border border-brand-700 z-9999">
                                    <div className="py-2 flex flex-col gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleUseCasesNavigation('/use-cases/peos-payroll-employment-organizations')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            PEOs
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleUseCasesNavigation('/use-cases/general-agents')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            General Agents
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleUseCasesNavigation('/use-cases/tpas-third-party-administrators')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            TPAs
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleUseCasesNavigation('/use-cases/fmos')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            FMOs
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleUseCasesNavigation('/use-cases/brokers')}
                                            className="text-left rounded-lg px-2 py-2 text-base hover:bg-brand-100 hover:text-brand-700"
                                        >
                                            Brokers
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tablet Button - Only Book a Demo */}
                    <div className='hidden md:flex lg:hidden'>
                        <Link to="/demo">
                            <Button variant="primary" type="button" size="md">
                                Book a Demo
                            </Button>
                        </Link>
                    </div>

                    {/* Desktop Buttons - Both buttons */}
                    <div className='hidden lg:flex gap-4'>
                        <Link to="/demo">
                            <Button variant="primary" type="button" size="md">
                                Book a Demo
                            </Button>
                        </Link>
                    </div>

                    {/* Mobile Burger Button */}
                    <button
                        onClick={toggleMenu}
                        className="md:hidden p-2 text-gray-700 hover:text-blue-600 transition-colors border-gray-200 border-1 rounded-lg"
                        aria-label="Toggle menu"
                    >
                        <Icon src={isMenuOpen ? closeIcon : menuIcon} className="size-6" />
                    </button>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="md:hidden mt-4 pb-5 relative">
                        <div className="bg-brand-100 absolute left-0 right-0 px-4 py-1" style={{ left: 'calc(-50vw + 50%)', right: 'calc(-50vw + 50%)', width: '100vw' }}>
                            <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <Icon src={phoneIcon} className="size-4 text-primary" />
                                    <a href="tel:+1-888-777-8190" className="text-sm text-primary">+1-888-777-8190</a>
                                </div>
                                <Link to="https://app.predictablebenefits.com/auth/login" className="text-sm text-primary" onClick={closeMenu}>
                                    Log In
                                </Link>
                            </div>
                        </div>
                        <div className="flex flex-col gap-4 pt-4 mt-[10px] items-center">
                            <Link
                                to="/"
                                className="px-3 py-2 rounded-lg transition-all hover:bg-brand-50 hover:text-brand-700 text-center"
                                onClick={closeMenu}
                            >
                                Home
                            </Link>
                            <div className="w-full">
                                <button
                                    onClick={handleShopPlansDropdownToggle}
                                    className={`flex items-center justify-center gap-1 w-full px-3 py-2 rounded-lg transition-all hover:bg-brand-50 hover:text-brand-700 ${isShopPlansDropdownOpen ? 'border border-brand-700' : ''
                                        }`}
                                >
                                    <span>Shop Plans</span>
                                    <Icon
                                        src={chevronIcon}
                                        className={`size-4 transition-transform duration-200 ${isShopPlansDropdownOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {isShopPlansDropdownOpen && (
                                    <div className="mt-2 w-full bg-gray-50 rounded-lg border border-brand-700">
                                        <div className="py-2">
                                            <button
                                                onClick={() => handleTabNavigation('age-zip')}
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-blue-700"
                                            >
                                                Search by Zip & Age
                                            </button>
                                            <button
                                                onClick={() => handleTabNavigation('state-county')}
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-blue-700"
                                            >
                                                Browse by State & County
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <Link
                                to="/pricing"
                                className="px-3 py-2 rounded-lg transition-all hover:bg-brand-50 hover:text-brand-700 text-center"
                                onClick={closeMenu}
                            >
                                Pricing
                            </Link>
                            <div className="w-full">
                                <button
                                    type="button"
                                    onClick={handleCompanyDropdownToggle}
                                    className={`flex items-center justify-center gap-1 w-full px-3 py-2 rounded-lg transition-all hover:bg-brand-50 hover:text-brand-700 ${isCompanyDropdownOpen ? 'border border-brand-700' : ''
                                        }`}
                                >
                                    <span>Company</span>
                                    <Icon
                                        src={chevronIcon}
                                        className={`size-4 transition-transform duration-200 ${isCompanyDropdownOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {isCompanyDropdownOpen && (
                                    <div className="mt-2 w-full bg-gray-50 rounded-lg border border-brand-700">
                                        <div className="py-2">
                                            <button
                                                onClick={() => handleMobileCompanyTabNavigation('overview')}
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                Overview
                                            </button>
                                            <button
                                                onClick={() => handleMobileCompanyTabNavigation('founders')}
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                Founders
                                            </button>
                                            <button
                                                onClick={() => handleMobileCompanyTabNavigation('advisory-board')}
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                Advisory Board
                                            </button>
                                            <button
                                                onClick={() => handleMobileCompanyTabNavigation('press')}
                                                className="w-full text-left px-4 py-2 text-sm hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                Press
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="w-full">
                                <button
                                    type="button"
                                    onClick={handleUseCasesDropdownToggle}
                                    className={`flex items-center justify-center gap-1 w-full px-3 py-2 rounded-lg transition-all hover:bg-brand-50 hover:text-brand-700 ${isUseCasesDropdownOpen ? 'border border-brand-700' : ''
                                        }`}
                                >
                                    <span>Use Cases</span>
                                    <Icon
                                        src={chevronIcon}
                                        className={`size-4 transition-transform duration-200 ${isUseCasesDropdownOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {isUseCasesDropdownOpen && (
                                    <div className="mt-2 w-full bg-gray-50 rounded-lg border border-brand-700">
                                        <div className="py-2">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleMobileUseCasesNavigation(
                                                        '/use-cases/overview',
                                                    )
                                                }
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                Overview
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleMobileUseCasesNavigation(
                                                        '/use-cases/peos-payroll-employment-organizations',
                                                    )
                                                }
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                PEOs
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleMobileUseCasesNavigation(
                                                        '/use-cases/general-agents',
                                                    )
                                                }
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                General Agents
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleMobileUseCasesNavigation(
                                                        '/use-cases/tpas-third-party-administrators',
                                                    )
                                                }
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                TPAs
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleMobileUseCasesNavigation(
                                                        '/use-cases/fmos',
                                                    )
                                                }
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                FMOs
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleMobileUseCasesNavigation(
                                                        '/use-cases/brokers',
                                                    )
                                                }
                                                className="w-full text-left px-4 py-2 text-sm transition-colors hover:bg-brand-100 hover:text-brand-700"
                                            >
                                                Brokers
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-3 pt-2 w-full">
                                <Link to="/demo" onClick={closeMenu}>
                                    <Button variant="primary" type="button" size="md" className="w-full">
                                        Book a Demo
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
}
